import { useEffect, useMemo, useState } from 'react';
import { Building2, Plus, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { organizationsAPI, usersAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';

const emptyFacilityDraft = {
  name: '',
  type: 'other',
  status: 'active',
  website: '',
  phone: '',
  description: '',
  address: { street: '', city: '', state: '', zip: '' },
  primaryContact: { name: '', title: '', email: '', phone: '' },
};

export default function FacilitiesPage() {
  const { user } = useAuthStore();
  const organizationId = user?.organizationId?._id || user?.organizationId;
  const canCreateOrEdit = user?.role === 'org_admin';
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [draft, setDraft] = useState(emptyFacilityDraft);
  const [creatingNew, setCreatingNew] = useState(false);
  const [facilityAdmins, setFacilityAdmins] = useState([]);

  const selectedFacility = useMemo(
    () => facilities.find((facility) => String(facility._id) === String(selectedFacilityId)) || null,
    [facilities, selectedFacilityId]
  );

  const loadFacilityAdmins = async () => {
    try {
      const res = await usersAPI.getAll();
      const all = Array.isArray(res?.data?.users) ? res.data.users : (Array.isArray(res?.data) ? res.data : []);
      setFacilityAdmins(all.filter((u) => u.role === 'facility_admin' && u.isActive !== false));
    } catch {
      // non-fatal
    }
  };

  const loadFacilities = async () => {
    try {
      setLoading(true);
      const res = await organizationsAPI.getMyFacilities();
      const list = Array.isArray(res?.data?.facilities) ? res.data.facilities : [];
      setFacilities(list);
      if (!list.length) {
        setCreatingNew(true);
        setSelectedFacilityId('');
        setDraft(emptyFacilityDraft);
      } else if (!selectedFacilityId || !list.some((facility) => String(facility._id) === String(selectedFacilityId))) {
        const first = list[0];
        setSelectedFacilityId(String(first._id));
        setCreatingNew(false);
        setDraft({
          name: first.name || '',
          type: first.type || 'other',
          status: first.status || 'active',
          website: first.website || '',
          phone: first.phone || '',
          description: first.description || '',
          address: first.address || { street: '', city: '', state: '', zip: '' },
          primaryContact: first.primaryContact || { name: '', title: '', email: '', phone: '' },
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      loadFacilities();
      loadFacilityAdmins();
    } else {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!selectedFacility) return;
    setDraft({
      name: selectedFacility.name || '',
      type: selectedFacility.type || 'other',
      status: selectedFacility.status || 'active',
      website: selectedFacility.website || '',
      phone: selectedFacility.phone || '',
      description: selectedFacility.description || '',
      address: selectedFacility.address || { street: '', city: '', state: '', zip: '' },
      primaryContact: selectedFacility.primaryContact || { name: '', title: '', email: '', phone: '' },
    });
  }, [selectedFacilityId, selectedFacility]);

  const startCreate = () => {
    setCreatingNew(true);
    setSelectedFacilityId('');
    setDraft(emptyFacilityDraft);
  };

  const selectFacility = (facility) => {
    setCreatingNew(false);
    setSelectedFacilityId(String(facility._id));
  };

  // After saving, map the facility_admin whose email matches primaryContact.email to this facility.
  // Also removes the old admin's mapping if the primary contact email changed.
  const syncFacilityAdminByEmail = async (facilityId, primaryContactEmail) => {
    const email = String(primaryContactEmail || '').trim().toLowerCase();
    const newAdmin = email ? facilityAdmins.find((u) => String(u.email || '').trim().toLowerCase() === email) : null;
    const prevAdmin = facilityAdmins.find((u) =>
      (u.clientStaffProfile?.facilityIds || []).some((id) => String(id) === String(facilityId)) &&
      String(u._id) !== String(newAdmin?._id || '')
    );
    if (prevAdmin) {
      const remaining = (prevAdmin.clientStaffProfile?.facilityIds || [])
        .map(String)
        .filter((id) => id !== String(facilityId));
      await usersAPI.update(prevAdmin._id, { facilityIds: remaining });
    }
    if (newAdmin) {
      const current = (newAdmin.clientStaffProfile?.facilityIds || []).map(String);
      if (!current.includes(String(facilityId))) {
        await usersAPI.update(newAdmin._id, { facilityIds: [...current, String(facilityId)] });
      }
    }
    await loadFacilityAdmins();
  };

  const handleSave = async () => {
    if (!organizationId) return;
    if (!String(draft.name || '').trim()) {
      toast.error('Facility name is required');
      return;
    }
    try {
      setSaving(true);
      if (creatingNew) {
        const res = await organizationsAPI.createFacility(organizationId, draft);
        toast.success('Facility created');
        const created = res?.data?.facility;
        await loadFacilities();
        if (created?._id) {
          setSelectedFacilityId(String(created._id));
          setCreatingNew(false);
          await syncFacilityAdminByEmail(created._id, draft.primaryContact?.email);
        }
      } else if (selectedFacilityId) {
        await organizationsAPI.updateFacility(organizationId, selectedFacilityId, draft);
        await syncFacilityAdminByEmail(selectedFacilityId, draft.primaryContact?.email);
        toast.success('Facility updated');
        await loadFacilities();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save facility');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-secondary-200 bg-white p-8 text-secondary-500">Loading facilities...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Facilities</h1>
          <p className="mt-1 text-secondary-600">Headquarters manages the facility registry for this organization, including contact details and operational metadata used across staffing, LMS, and consulting workflows.</p>
        </div>
        {canCreateOrEdit && (
          <button type="button" onClick={startCreate} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700">
            <Plus size={18} />
            Add Facility
          </button>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[320px,1fr]">
        <div className="rounded-xl border border-secondary-200 bg-white">
          <div className="border-b border-secondary-200 px-5 py-4">
            <h2 className="font-semibold text-secondary-900">Facility List</h2>
          </div>
          <div className="space-y-3 p-4">
            {facilities.length === 0 ? (
              <div className="rounded-lg border border-dashed border-secondary-200 p-4 text-sm text-secondary-500">
                No facilities on file yet.
              </div>
            ) : (
              facilities.map((facility) => {
                const active = !creatingNew && String(selectedFacilityId) === String(facility._id);
                return (
                  <button
                    key={facility._id}
                    type="button"
                    onClick={() => selectFacility(facility)}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition ${
                      active
                        ? 'border-primary-300 bg-primary-50 text-primary-900'
                        : 'border-secondary-200 bg-white text-secondary-800 hover:border-secondary-300 hover:bg-secondary-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-secondary-100 p-2 text-secondary-700">
                        <Building2 size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{facility.name}</p>
                        <p className="truncate text-xs text-secondary-500">
                          {[facility.type, facility.address?.city, facility.address?.state].filter(Boolean).join(' · ') || 'No location details'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="rounded-xl border border-secondary-200 bg-white">
          <div className="border-b border-secondary-200 px-5 py-4">
            <h2 className="font-semibold text-secondary-900">{creatingNew ? 'Create Facility' : 'Facility Details'}</h2>
          </div>
          <div className="space-y-5 p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="label">Facility Name</label>
                <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div>
                <label className="label">Type</label>
                <select className="input" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}>
                  <option value="other">Other</option>
                  <option value="nursing_home">Nursing Home</option>
                  <option value="assisted_living">Assisted Living</option>
                  <option value="clinic">Clinic</option>
                  <option value="hospital">Hospital</option>
                  <option value="rehab_center">Rehab Center</option>
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="label">Status</label>
                <select className="input" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label className="label">Phone</label>
                <input className="input" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="label">Website</label>
                <input className="input" value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} />
              </div>
              <div>
                <label className="label">Street Address</label>
                <input className="input" value={draft.address?.street || ''} onChange={(e) => setDraft({ ...draft, address: { ...(draft.address || {}), street: e.target.value } })} />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="label">City</label>
                <input className="input" value={draft.address?.city || ''} onChange={(e) => setDraft({ ...draft, address: { ...(draft.address || {}), city: e.target.value } })} />
              </div>
              <div>
                <label className="label">State</label>
                <input className="input" value={draft.address?.state || ''} onChange={(e) => setDraft({ ...draft, address: { ...(draft.address || {}), state: e.target.value } })} />
              </div>
              <div>
                <label className="label">ZIP</label>
                <input className="input" value={draft.address?.zip || ''} onChange={(e) => setDraft({ ...draft, address: { ...(draft.address || {}), zip: e.target.value } })} />
              </div>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea className="input min-h-[96px]" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>

            <div className="rounded-lg border border-secondary-200 p-4">
              <h3 className="font-medium text-secondary-900">Primary Contact</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="label">Contact Name</label>
                  <input className="input" value={draft.primaryContact?.name || ''} onChange={(e) => setDraft({ ...draft, primaryContact: { ...(draft.primaryContact || {}), name: e.target.value } })} />
                </div>
                <div>
                  <label className="label">Title</label>
                  <input className="input" value={draft.primaryContact?.title || ''} onChange={(e) => setDraft({ ...draft, primaryContact: { ...(draft.primaryContact || {}), title: e.target.value } })} />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input className="input" value={draft.primaryContact?.email || ''} onChange={(e) => setDraft({ ...draft, primaryContact: { ...(draft.primaryContact || {}), email: e.target.value } })} />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" value={draft.primaryContact?.phone || ''} onChange={(e) => setDraft({ ...draft, primaryContact: { ...(draft.primaryContact || {}), phone: e.target.value } })} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              {creatingNew && (
                <button type="button" onClick={() => facilities.length ? selectFacility(facilities[0]) : setDraft(emptyFacilityDraft)} className="rounded-lg px-4 py-2 text-secondary-700 hover:bg-secondary-100">
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={!canCreateOrEdit || saving}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <Save size={18} />
                {saving ? 'Saving...' : creatingNew ? 'Create Facility' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
